import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ResponsiveTable, { ResponsiveTableColumn } from '../components/common/ResponsiveTable';
import MonthField from '../components/common/MonthField';
import apiService from '../services/api.service';
import { MonthProjectRow, ReportConfig } from '../types/projectReport';
import { currentMonth, monthLabel } from '../utils/projectReport';

/**
 * Projects > Monthly Reports: the selected company's projects for one month, each with its progress
 * report (prepared from SAP / MCSR and the previous month's report) or a button to prepare it.
 */

type StatusFilter = 'all' | 'missing' | 'Draft' | 'Submitted';

const errorMessage = (e: any, fallback: string): string => e?.response?.data?.message || e?.message || fallback;

const ProjectReports: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const companyId = useMemo(() => Number(localStorage.getItem('selectedCompanyId') || '') || 0, []);
  const month = /^\d{4}-\d{2}$/.test(params.get('month') || '') ? (params.get('month') as string) : currentMonth();
  const [config, setConfig] = useState<ReportConfig | null>(null);
  const [rows, setRows] = useState<MonthProjectRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [creating, setCreating] = useState<string | null>(null);

  useEffect(() => {
    if (!companyId) {
      setError('Select a company first.');
      return undefined;
    }
    let cancelled = false;
    apiService.getProjectReportConfig(companyId)
      .then((res: any) => { if (!cancelled) setConfig(res.data); })
      .catch((e: any) => { if (!cancelled) setError(errorMessage(e, 'The report settings could not be loaded')); });
    return () => { cancelled = true; };
  }, [companyId]);

  const load = useCallback(() => {
    if (!companyId || !config?.template) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiService.getProjectReportMonth(companyId, month)
      .then((res: any) => { if (!cancelled) setRows(Array.isArray(res?.data?.rows) ? res.data.rows : []); })
      .catch((e: any) => { if (!cancelled) setError(errorMessage(e, 'The projects could not be loaded')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [companyId, config?.template, month]);

  useEffect(() => load(), [load]);

  const create = async (projectCode: string) => {
    setCreating(projectCode);
    setError(null);
    try {
      const res: any = await apiService.createProjectReport(companyId, projectCode, month);
      navigate(`/project-reports/${res.data.reportId}`);
    } catch (e: any) {
      setError(errorMessage(e, 'The report could not be created'));
      setCreating(null);
    }
  };

  const counts = useMemo(() => ({
    all: rows.length,
    missing: rows.filter((r) => !r.report).length,
    Draft: rows.filter((r) => r.report?.status === 'Draft').length,
    Submitted: rows.filter((r) => r.report?.status === 'Submitted').length,
  }), [rows]);

  const visible = useMemo(() => {
    const words = search.toLowerCase().split(/\s+/).filter(Boolean);
    return rows.filter((r) => {
      if (status === 'missing' && r.report) return false;
      if ((status === 'Draft' || status === 'Submitted') && r.report?.status !== status) return false;
      const hay = `${r.projectCode} ${r.projectName}`.toLowerCase();
      return words.every((w) => hay.includes(w));
    });
  }, [rows, search, status]);

  const columns: ResponsiveTableColumn<MonthProjectRow>[] = [
    {
      id: 'project',
      header: 'Project',
      cell: (r) => (
        <Box>
          <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>{r.projectCode}</Typography>
          <Typography variant="caption" color="text.secondary">{r.projectName}</Typography>
        </Box>
      ),
    },
    {
      id: 'report',
      header: 'Report',
      cell: (r) => (r.report ? (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body2">No. {r.report.reportNo}</Typography>
          <Chip size="small" label={r.report.status} color={r.report.status === 'Submitted' ? 'success' : 'warning'} variant={r.report.status === 'Submitted' ? 'filled' : 'outlined'} />
          {r.report.critical > 0 && (
            <Chip size="small" icon={<ReportProblemIcon />} label={`${r.report.critical} critical`} sx={{ bgcolor: '#b71c1c', color: '#fff', '& .MuiChip-icon': { color: '#fff' } }} />
          )}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">Not prepared</Typography>
      )),
    },
    { id: 'by', header: 'Prepared by', cell: (r) => r.report?.preparedBy || '', hideOnMobile: true },
    {
      id: 'updated',
      header: 'Last change',
      cell: (r) => (r.report ? new Date(r.report.updatedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : ''),
      hideOnMobile: true,
    },
    {
      id: 'action',
      header: '',
      align: 'right',
      cell: (r) => (r.report ? (
        <Button size="small" onClick={(e) => { e.stopPropagation(); navigate(`/project-reports/${r.report?.reportId}`); }}>Open</Button>
      ) : (
        <Button
          size="small"
          variant="contained"
          startIcon={creating === r.projectCode ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
          disabled={!!creating}
          onClick={(e) => { e.stopPropagation(); void create(r.projectCode); }}
        >
          {creating === r.projectCode ? 'Reading SAP…' : 'Prepare report'}
        </Button>
      )),
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: 'grid', gap: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ sm: 'center' }}>
        <Box>
          <Typography variant="h5" component="h1">Project Monthly Reports</Typography>
          <Typography variant="body2" color="text.secondary">
            {config?.template
              ? `${config.companyName} · ${config.templateLabel} template · ${monthLabel(month)}`
              : 'The monthly progress report of each project, prefilled from SAP and MCSR.'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <MonthField label="Month" value={month} max={currentMonth()} onChange={(m) => setParams({ month: m })} />
          {config?.access.issues && <Button variant="outlined" onClick={() => navigate('/project-issues')}>Project Issues</Button>}
        </Stack>
      </Stack>

      {config && !config.template && (
        <Alert severity="info">
          Monthly project reports are not set up for this company. An administrator chooses the template (Engie KSA or Dhow Kuwait) in Company Management.
        </Alert>
      )}
      {config?.template && config.access.projects && config.access.projects.length === 0 && (
        <Alert severity="info">No project of this company is granted to you yet (User Management › Access).</Alert>
      )}
      {error && <Alert severity="warning" onClose={() => setError(null)}>{error}</Alert>}

      {config?.template && (
        <>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
            <TextField
              size="small"
              placeholder="Search project code or name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              sx={{ minWidth: { md: 320 } }}
              inputProps={{ 'aria-label': 'Search projects' }}
            />
            <ToggleButtonGroup size="small" exclusive value={status} onChange={(_e, v) => v && setStatus(v)} aria-label="Report status">
              <ToggleButton value="all">All ({counts.all})</ToggleButton>
              <ToggleButton value="missing">Not prepared ({counts.missing})</ToggleButton>
              <ToggleButton value="Draft">Draft ({counts.Draft})</ToggleButton>
              <ToggleButton value="Submitted">Submitted ({counts.Submitted})</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          {loading ? (
            <Stack direction="row" spacing={1.5} alignItems="center" role="status"><CircularProgress size={20} /><Typography>Loading projects…</Typography></Stack>
          ) : (
            <ResponsiveTable
              ariaLabel="Projects and their monthly reports"
              columns={columns}
              rows={visible}
              getRowKey={(r) => r.projectCode}
              onRowClick={(r) => { if (r.report) navigate(`/project-reports/${r.report.reportId}`); }}
              emptyMessage={rows.length ? 'No project matches.' : 'No project.'}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default ProjectReports;
